import * as React from "react";
import { Loader2, Shield, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import {
  addBrandMember,
  removeBrandMember,
  updateBrandMemberRole,
  type AdminBrandRow,
  type AdminMember,
} from "@/data/brands-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/** Round member avatar — photo, falling back to initials. */
function MemberAvatar({ member }: { member: AdminMember }) {
  const [broken, setBroken] = React.useState(false);
  if (member.photoURL && !broken) {
    return (
      <img
        src={member.photoURL}
        alt={member.name}
        onError={() => setBroken(true)}
        className="size-9 shrink-0 rounded-full border border-border object-cover"
      />
    );
  }
  return (
    <div className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-gradient text-[12px] font-bold text-white">
      {member.name.slice(0, 2).toUpperCase()}
    </div>
  );
}

/**
 * Admin management of a brand's members: add by username, flip OWNER/MEMBER, and
 * remove. Open when `row` is non-null. Every change calls the backend and reports
 * the resulting member list up via {@link onMembersChange} so the directory row
 * stays in sync without a full refetch.
 */
export function MembersDialog({
  row,
  onOpenChange,
  onMembersChange,
}: {
  row: AdminBrandRow | null;
  onOpenChange: (open: boolean) => void;
  onMembersChange: (brandId: string, members: AdminMember[]) => void;
}) {
  const [members, setMembers] = React.useState<AdminMember[]>([]);
  const [username, setUsername] = React.useState("");
  const [adding, setAdding] = React.useState(false);
  // Per-member busy flag (role change / removal) keyed by member id.
  const [busyId, setBusyId] = React.useState<string | null>(null);

  // Reseed whenever the target brand changes (or the dialog reopens).
  React.useEffect(() => {
    setMembers(row?.members ?? []);
    setUsername("");
  }, [row?.id, row?.members]);

  if (!row) return null;
  const brandId = row.id;
  const ownerCount = members.filter((m) => m.role === "OWNER").length;

  function commit(next: AdminMember[]) {
    setMembers(next);
    onMembersChange(brandId, next);
  }

  async function add() {
    const uname = username.trim().replace(/^@/, "");
    if (!uname || adding) return;
    setAdding(true);
    try {
      const member = await addBrandMember({ brandId, username: uname, role: "MEMBER" });
      const mapped: AdminMember = {
        id: member.id,
        role: member.role,
        name: member.user.public?.displayName || member.user.public?.username || uname,
        username: member.user.public?.username ?? uname,
        photoURL: member.user.public?.photoURL ?? "",
      };
      // Replace if the user is somehow already listed, else append.
      commit([...members.filter((m) => m.id !== mapped.id), mapped]);
      setUsername("");
      toast.success(`Added ${mapped.name}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t add that member."));
    } finally {
      setAdding(false);
    }
  }

  async function setRole(member: AdminMember, role: "OWNER" | "MEMBER") {
    if (member.role === role || busyId) return;
    // Don't let the last owner demote themselves to member — the brand would be
    // left ownerless. (The backend enforces this too; we guard for a clean UX.)
    if (role === "MEMBER" && member.role === "OWNER" && ownerCount <= 1) {
      toast.error("A brand needs at least one owner.");
      return;
    }
    setBusyId(member.id);
    try {
      await updateBrandMemberRole({ brandId, memberId: member.id, role });
      commit(members.map((m) => (m.id === member.id ? { ...m, role } : m)));
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t change that role."));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(member: AdminMember) {
    if (busyId) return;
    if (member.role === "OWNER" && ownerCount <= 1) {
      toast.error("Can’t remove the last owner.");
      return;
    }
    setBusyId(member.id);
    try {
      await removeBrandMember(brandId, member.id);
      commit(members.filter((m) => m.id !== member.id));
      toast.success(`Removed ${member.name}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t remove that member."));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Dialog open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-border-strong bg-popover shadow-portal sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="size-[18px] text-brand" />
            Members of {row.name}
          </DialogTitle>
          <DialogDescription>
            Add or remove members and set who owns this brand. Owners can manage members and delete
            the brand.
          </DialogDescription>
        </DialogHeader>

        {/* Add by username */}
        <div className="space-y-[7px]">
          <Label htmlFor="add-member-username">Add a member by username</Label>
          <div className="flex gap-2">
            <Input
              id="add-member-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              placeholder="@username"
              onKeyDown={(e) => {
                if (e.key === "Enter") void add();
              }}
            />
            <Button onClick={() => void add()} disabled={!username.trim() || adding} className="shrink-0">
              {adding ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
              Add
            </Button>
          </div>
        </div>

        {/* Member list */}
        <div className="mt-1 max-h-[320px] space-y-1.5 overflow-y-auto pr-0.5">
          {members.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border-strong py-8 text-center text-[13px] text-text-3">
              No members yet — add one above.
            </div>
          ) : (
            members.map((m) => {
              const busy = busyId === m.id;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-2.5"
                >
                  <MemberAvatar member={m} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold text-foreground">{m.name}</div>
                    {m.username && (
                      <div className="truncate text-[11.5px] text-text-3">@{m.username}</div>
                    )}
                  </div>

                  {/* OWNER / MEMBER segmented toggle */}
                  <div className="flex shrink-0 rounded-[9px] border border-border bg-field p-[3px]">
                    {(["MEMBER", "OWNER"] as const).map((role) => (
                      <button
                        key={role}
                        type="button"
                        disabled={busy}
                        onClick={() => void setRole(m, role)}
                        className={cn(
                          "rounded-[6px] px-2.5 py-1 text-[11px] font-bold tracking-[.02em] transition-all disabled:opacity-50",
                          m.role === role
                            ? "bg-card text-foreground shadow-portal"
                            : "text-text-3 hover:text-foreground",
                        )}
                      >
                        {role === "OWNER" ? "Owner" : "Member"}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    title="Remove member"
                    disabled={busy}
                    onClick={() => void remove(m)}
                    className="grid size-8 shrink-0 place-items-center rounded-lg border border-border bg-panel-2 text-text-3 transition-colors hover:border-[rgba(255,120,120,.35)] hover:text-[#ff7a7a] disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
